import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import Collapse from 'bootstrap/js/dist/collapse';
import { FormGroup } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ClinicalTrialsUIService } from '../../services/clinical-trials-ui.service';
import { getCriteriaAnswers, isInformationalCriteria } from '../../utils';
import { ICriteria, ICriteriaLabel } from '../../interfaces';
import { IItemAnswerCode } from '../../types';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-criteria',
  styleUrls: ['./criteria.component.scss'],
  templateUrl: './criteria.component.html'
})
export class CriteriaComponent implements OnInit {

  @Input()
  group: fhir4.Group;

  @Input()
  index: number;

  @Input()
  questionnaireResponse: fhir4.QuestionnaireResponse;

  @Input()
  qualityScoreForm: FormGroup;

  collapse: Collapse;
  inclusionRequiredCriteria: ICriteria;
  inclusionOptionalCriteria: ICriteria;
  inclusionInformationalCriteria: Pick<ICriteria, 'criteriaLabels' | 'expanderID'>;
  exclusionRequiredCriteria: ICriteria;
  exclusionOptionalCriteria: ICriteria;
  exclusionInformationalCriteria: Pick<ICriteria, 'criteriaLabels' | 'expanderID'>;

  constructor(
    private readonly clinicalTrialsUIService: ClinicalTrialsUIService
  ) {}

  ngOnInit(): void {
    this.mapInclusionRequiredAnswers();
    this.mapInclusionOptionalQuestions();
    this.mapExclusionRequiredQuestions();
    this.mapExclusionOptionalQuestions();
    this.mapInformationalCriteria();

    this.clinicalTrialsUIService.criteriaChanged$.pipe(untilDestroyed(this)).subscribe(() => {
      this.qualityScoreForm.get('valueRatio.numerator.value').patchValue(this.inclusionRequiredCriteria.criteriaMatched + this.exclusionRequiredCriteria.criteriaMatched);
      const eligible = ((this.inclusionRequiredCriteria?.criteriaNotMatched || 0) + (this.exclusionRequiredCriteria?.criteriaNotMatched || 0)) === 0;

      this.clinicalTrialsUIService.onEligibilityUpdated(eligible);
      this.qualityScoreForm.markAsDirty();
    });
  }

  private mapInclusionRequiredAnswers(): void {
    const inclusionRequiredQuestions = this.group.characteristic.filter(c => c.valueBoolean && !c.exclude && !isInformationalCriteria(c.extension));
    const { ids, labels } = this.mapProperties(inclusionRequiredQuestions);
    const criteriaAnswers = getCriteriaAnswers(this.questionnaireResponse?.item, ids);
    const criteriaMatchType: IItemAnswerCode = 'met';
    const criteriaNoMatchType: IItemAnswerCode = 'not-met';

    this.inclusionRequiredCriteria = {
      criteriaAnswers,
      criteriaLabels: labels,
      criteriaMatched: 0,
      criteriaMatchType,
      criteriaNoMatchType,
      expanderID: `inclusion-required-${ this.index }`,
      inclusion: true,
      required: true,
    };
  }

  private mapInclusionOptionalQuestions(): void {
    const inclusionOptionalQuestions = this.group.characteristic.filter(c => !c.valueBoolean && !c.exclude && !isInformationalCriteria(c.extension));
    const { ids, labels } = this.mapProperties(inclusionOptionalQuestions);

    this.inclusionOptionalCriteria = {
      criteriaAnswers: getCriteriaAnswers(this.questionnaireResponse?.item, ids),
      criteriaLabels: labels,
      criteriaMatchType: 'met',
      criteriaNoMatchType: 'not-met',
      expanderID: `inclusion-optional-${ this.index }`,
      inclusion: true,
      required: false
    };
  }

  private mapExclusionRequiredQuestions(): void {
    const exclusionRequiredQuestions = this.group.characteristic.filter(c => c.valueBoolean && c.exclude && !isInformationalCriteria(c.extension));
    const { ids, labels } = this.mapProperties(exclusionRequiredQuestions);
    const criteriaAnswers = getCriteriaAnswers(this.questionnaireResponse?.item, ids);
    const criteriaMatchType: IItemAnswerCode = 'not-met';
    const criteriaNoMatchType: IItemAnswerCode = 'met';

    this.exclusionRequiredCriteria = {
      criteriaAnswers,
      criteriaLabels: labels,
      criteriaMatched: 0,
      criteriaMatchType,
      criteriaNoMatchType,
      expanderID: `exclusion-required-${ this.index }`,
      inclusion: false,
      required: true,
    };
  }

  private mapExclusionOptionalQuestions(): void {
    const exclusionOptionalQuestions = this.group.characteristic.filter(c => !c.valueBoolean && c.exclude && !isInformationalCriteria(c.extension));
    const { ids, labels } = this.mapProperties(exclusionOptionalQuestions);

    this.exclusionOptionalCriteria = {
      criteriaAnswers: getCriteriaAnswers(this.questionnaireResponse?.item, ids),
      criteriaLabels: labels,
      criteriaMatchType: 'not-met',
      criteriaNoMatchType: 'met',
      expanderID: `exclusion-optional-${ this.index }`,
      inclusion: false,
      required: false
    };
  }

  private mapInformationalCriteria(): void {
    const exclusionCriteria = this.group.characteristic.filter(c => c.exclude && isInformationalCriteria(c.extension));
    const inclusionCriteria = this.group.characteristic.filter(c => !c.exclude && isInformationalCriteria(c.extension));

    this.exclusionInformationalCriteria = {
      criteriaLabels: this.mapProperties(exclusionCriteria).labels,
      expanderID: `exclusion-informational-${ this.index }`,
    };
    this.inclusionInformationalCriteria = {
      criteriaLabels: this.mapProperties(inclusionCriteria).labels,
      expanderID: `inclusion-informational-${ this.index }`,
    };
  }

  private mapProperties(groupCharacteristic: fhir4.GroupCharacteristic[]): { ids: string[], labels: ICriteriaLabel[] } {
    const ids: string[] = [];
    const labels: ICriteriaLabel[] = [];

    groupCharacteristic.forEach(obj => {
      ids.push(obj.id);
      labels.push({
        label: obj.extension[0].valueMarkdown,
        linkId: obj.id
      });
    });

    return {
      ids,
      labels,
    };
  }
}
